<template>
  <SlideIn
    ref="modal"
    :title="step === 2 ? selectedObject?.name : t('create_ticket')"
    :is-subpage="step === 2"
    :open="open"
    @close="() => EventBus.$emit('CREATETICKET_CLOSE')"
    @back="goBack"
  >
    <template v-if="step === 2" #actions>
      <Button :variant="ButtonVariant.Primary" :disabled="loading" @click="createTicket">{{
        t("create_ticket")
      }}</Button>
    </template>
    <div class="grow overflow-auto">
      <WizardCreateTicketStepExistingTicket v-if="ticketId" :ticket-id="ticketId" />
      <WizardCreateTicketStepPackageNumber v-if="step === 0" @submit="startTicket" />
      <WizardCreateTicketStepSelectType
        v-if="types && types.length !== 0 && step === 1"
        :types="types"
        @select="(val) => selectType(val)"
      />
      <p v-if="isBulk && step === 1 && !types.length && !loading" class="text-body-default p-5">
        {{ t("bulk_tickets.no_types") }}
      </p>
      <template v-if="step === 2 && selectedObject">
        <WizardCreateTicketStepAttributes
          v-model:message="ticket.message"
          :attributes="isBulk ? [] : selectedObject.attributes"
          :shipment-id="shipmentId"
          @attribute-change="(val, attribute) => addAttributeValue(val, attribute)"
        >
          <InputTextarea
            v-model="ticket.message"
            :error="messageError"
            :label="t('ticket_description')"
            @blur="onMessageBlur"
          />
        </WizardCreateTicketStepAttributes>
      </template>
      <WizardCreateTicketStepBulkResult v-if="step === 3 && bulkResult" :result="bulkResult" @reset="reset" />
      <Overlay v-if="loading" class="absolute inset-x-0 top-0 left-0 h-lvh min-h-[300px] w-full" />
    </div>
  </SlideIn>
</template>

<script setup lang="ts">
  import type {
    AttributeWithError,
    BulkTicketResult,
    ITicketExistsResponse,
    ITicketTypesResponse,
    TicketAttributeValue,
    TicketType,
  } from "~/types/ticket";

  const { t } = useI18n();

  const open = ref(false);
  const loading = ref(false);
  const types = ref<TicketType[]>([]);
  const packageNumber = ref("");
  const shipmentId = ref("");
  const selectedType = ref("");
  const step = ref(0);
  const ticketId = ref<number>();
  const newTicketPackageNumber = ref("");
  // Several package numbers make one case with the shipments attached, so no attributes are collected
  const bulkPackageNumbers = ref<string[]>([]);
  const isBulk = computed(() => bulkPackageNumbers.value.length > 1);
  const bulkResult = ref<BulkTicketResult>();

  const ticket = ref<{
    shipment_id: string;
    package_number: string;
    type: string;
    from_name: string;
    from_email: string;
    cc: string;
    message: string;
    attributes: TicketAttributeValue[];
  }>({
    shipment_id: "",
    package_number: "",
    type: "",
    from_name: "",
    from_email: "",
    cc: "",
    message: "",
    attributes: [],
  });

  const selectedObject = computed(() => {
    if (!selectedType.value) {
      return null;
    }
    return types.value.find((type) => type.key === selectedType.value);
  });

  const { submitTicket, createNewTicket } = useTickets();

  const selectType = (type: TicketType) => {
    selectedType.value = type.key;
    ticket.value.type = type.state.toString();

    step.value = 2;
  };

  const goBack = () => {
    selectedObject.value?.attributes.forEach((attribute) => {
      (attribute as AttributeWithError).error = null;
    });
    ticket.value.attributes = [];
    ticket.value.message = "";
    messageError.value = undefined;
    step.value = 1;
  };

  const { userName } = storeToRefs(useWorkspacesStore());

  const startTicket = (packageNumbers: string[]) => {
    if (packageNumbers.length === 1) {
      createNewTicket(packageNumbers[0]!);
      return;
    }

    bulkPackageNumbers.value = packageNumbers;
    selectedType.value = "";
    ticket.value = { ...ticket.value, type: "", from_name: userName.value, message: "", attributes: [] };
    step.value = 1;
    loading.value = true;
    homeFetch<{ data: TicketType[] }>("tickets/bulk/types")
      .then((r) => {
        types.value = (r.data?.data ?? []).filter((type) => type.attributes.every((attribute) => attribute.optional));
      })
      .finally(() => (loading.value = false));
  };

  const reset = () => {
    ticketId.value = undefined;
    types.value = [];
    step.value = 0;
    newTicketPackageNumber.value = "";
    bulkPackageNumbers.value = [];
    bulkResult.value = undefined;
    messageError.value = undefined;
  };

  onMounted(() => {
    EventBus.$on("CREATETICKET_ADD", (data: ITicketTypesResponse | ITicketExistsResponse, packageNum: string) => {
      loading.value = true;
      bulkPackageNumbers.value = [];
      step.value = 1;
      selectedType.value = "";
      packageNumber.value = packageNum;
      if ((data as ITicketExistsResponse).ticket_id) {
        ticketId.value = (data as ITicketExistsResponse).ticket_id;
        types.value = [];
        loading.value = false;

        return;
      }

      types.value = (data as ITicketTypesResponse).data;
      ticket.value = {
        shipment_id: (data as ITicketTypesResponse).shipment_id.toString(),
        package_number: packageNum,
        type: "",
        from_name: userName.value,
        from_email: "",
        cc: "",
        message: "",
        attributes: [],
      };
      shipmentId.value = (data as ITicketTypesResponse).shipment_id.toString();

      loading.value = false;
    });

    EventBus.$on("CREATETICKET_OPEN", () => {
      open.value = true;
    });

    EventBus.$on("CREATETICKET_CLOSE", () => {
      open.value = false;
      reset();
    });
  });

  onUnmounted(() => {
    EventBus.$off("CREATETICKET_ADD");
    EventBus.$off("CREATETICKET_OPEN");
    EventBus.$off("CREATETICKET_CLOSE");
  });

  // #region Message Error Handling
  const messageError = ref<string>();
  const onMessageBlur = () => {
    if (!ticket.value.message || ticket.value.message.trim() === "") {
      messageError.value = t("required");
    }
  };
  // #endregion

  const addAttributeValue = (
    val: string | string[] | { name: string; value: string }[],
    attribute: AttributeWithError
  ) => {
    attribute.error = null;
    const attributeValues: Partial<TicketAttributeValue> = {
      key: attribute.key,
      type: attribute.type,
      type_attribute_id: attribute.id,
      value: null,
    };
    const existingAttributeIndex = ticket.value.attributes.findIndex((attr) => attr.key === attribute.key);
    if (existingAttributeIndex !== -1) {
      ticket.value.attributes.splice(existingAttributeIndex, 1);
    }

    if (!val && attribute.optional) {
      return;
    }

    if (Array.isArray(val) && val.length === 0) {
      return;
    } else {
      if (attribute.type === "file" || attribute.type === "dor" || attribute.type === "file_with_pdf") {
        if (Array.isArray(val) && val.length > 0 && typeof val[0] === "object" && "name" in val[0]) {
          attributeValues.fileName = val[0].name;
          attributeValues.value = val[0].value;
        }
      } else if (attribute.type === "file[]") {
        if (
          Array.isArray(val) &&
          val.every((v): v is { name: string; value: string } => typeof v === "object" && "name" in v)
        ) {
          attributeValues.fileNames = val.map((v) => v.name);
          attributeValues.value = val.map((v) => v.value);
        }
      } else {
        attributeValues.value = val as string | string[];
      }
    }
    ticket.value.attributes.push(attributeValues as TicketAttributeValue);
  };

  const submitBulk = () => {
    if (!ticket.value.message.trim()) {
      messageError.value = t("required");
      return;
    }
    loading.value = true;
    homeFetch<BulkTicketResult>("tickets/bulk", {
      method: "POST",
      body: {
        package_numbers: bulkPackageNumbers.value,
        type: ticket.value.type,
        from_name: ticket.value.from_name,
        message: ticket.value.message,
      },
    })
      .then((r) => {
        // Missing groups count as empty, so a claims build without one (e.g. mid-deploy) can't break the result screen
        bulkResult.value = {
          ticket_id: null,
          added: [],
          attached: [],
          existing: [],
          ineligible: [],
          not_found: [],
          ...r.data,
        };
        step.value = 3;
        if (bulkResult.value.ticket_id) createTicketCreated();
      })
      .finally(() => (loading.value = false));
  };

  const createTicket = () => {
    if (!selectedObject.value) return;
    if (isBulk.value) return submitBulk();

    const allFilled = selectedObject.value.attributes.every((attribute) => {
      if (attribute.optional) return true;

      return (
        ticket.value.attributes.some((attr) => attr.type_attribute_id === attribute.id && attr.value) &&
        ticket.value.message
      );
    });
    if (!allFilled) {
      //create array of missing attributes
      const missingAttributes = selectedObject.value.attributes.filter((attribute) => {
        return !ticket.value.attributes.some((attr) => attr.type_attribute_id === attribute.id && attr.value);
      });
      missingAttributes.forEach((element) => {
        const originalAttribute = selectedObject.value?.attributes.find((attr) => attr.id === element.id) as
          | AttributeWithError
          | undefined;
        if (originalAttribute) {
          originalAttribute.error = t("required");
        }
      });
      onMessageBlur();
      return;
    }
    loading.value = true;
    submitTicket(ticket.value)
      .then((response) => {
        if (response.ticketId) {
          createTicketCreated();
          createTicketClose();
        }
      })
      .finally(() => {
        loading.value = false;
      });
  };
</script>
